import { useState, useEffect, useCallback } from 'react'
import { API_URL } from '../config/api'
import { useAuthStore } from '../stores/authStore'

interface SessionTimeoutState {
  minutesRemaining: number | null
  isExpiringSoon: boolean
  isExpiryCritical: boolean
  isExpired: boolean
}

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1]))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

export function useSessionTimeout(): SessionTimeoutState & { refresh: () => void } {
  const accessToken = useAuthStore((s) => s.accessToken)
  const logout = useAuthStore((s) => s.logout)

  const [state, setState] = useState<SessionTimeoutState>({
    minutesRemaining: null,
    isExpiringSoon: false,
    isExpiryCritical: false,
    isExpired: false,
  })

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return

    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        const newAccessToken = data.access_token
        if (newAccessToken) {
          localStorage.setItem('access_token', newAccessToken)
          useAuthStore.getState().setTokens(newAccessToken, refreshToken)
        }
      }
    } catch {
      // Refresh failed silently -- will be caught by the next tick
    }
  }, [])

  useEffect(() => {
    if (!accessToken) {
      setState({
        minutesRemaining: null,
        isExpiringSoon: false,
        isExpiryCritical: false,
        isExpired: false,
      })
      return
    }

    const checkExpiry = () => {
      const exp = decodeJwtExp(accessToken)
      if (exp === null) return

      const nowSeconds = Math.floor(Date.now() / 1000)
      const remainingSeconds = exp - nowSeconds
      const remainingMinutes = Math.max(0, Math.ceil(remainingSeconds / 60))

      if (remainingSeconds <= 0) {
        setState({
          minutesRemaining: 0,
          isExpiringSoon: true,
          isExpiryCritical: true,
          isExpired: true,
        })
        logout()
        window.location.href = '/login'
        return
      }

      setState({
        minutesRemaining: remainingMinutes,
        isExpiringSoon: remainingMinutes <= 5,
        isExpiryCritical: remainingMinutes <= 1,
        isExpired: false,
      })
    }

    checkExpiry()
    const interval = setInterval(checkExpiry, 15_000) // check every 15s
    return () => clearInterval(interval)
  }, [accessToken, logout])

  return { ...state, refresh }
}
